/**
 * Pantalla de inicio de sesión.
 */
import React, { useEffect, useState } from 'react';
import { auth } from '../firebaseConfig';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { ink, studio } from './admin/studioUi';
import { useAuth } from '../AuthContext';
import AuthShell, {
    AUTH_API_URL,
    AuthField,
    AuthNotice,
    PasswordToggle,
    isValidEmail,
    normalizeEmail,
} from './AuthShell';

const FAIL_KEY = 'cm.login.fails';
const LOCK_KEY = 'cm.login.lockUntil';
const MAX_FAILS = 5;
const LOCK_MS = 60 * 1000;
const GENERIC_RESET = 'Si el correo está registrado, te enviaremos instrucciones. Revisa bandeja y spam.';

const loginErrorMessage = (code) => {
    if (code === 'auth/too-many-requests') return 'Demasiados intentos. Espera unos minutos e inténtalo de nuevo.';
    if (code === 'auth/network-request-failed') return 'No se pudo conectar. Revisa tu red e inténtalo de nuevo.';
    if (code === 'auth/user-disabled') return 'Esta cuenta no puede iniciar sesión. Contacta a un administrador.';
    return 'El correo o la contraseña son incorrectos.';
};

const Auth = () => {
    const { withBusy } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [fails, setFails] = useState(() => Number(sessionStorage.getItem(FAIL_KEY) || 0));
    const [lockUntil, setLockUntil] = useState(() => Number(sessionStorage.getItem(LOCK_KEY) || 0));
    const [now, setNow] = useState(Date.now());

    const locked = lockUntil > now;
    const remaining = locked ? Math.max(1, Math.ceil((lockUntil - now) / 1000)) : 0;

    useEffect(() => {
        if (!locked) return undefined;
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, [locked]);

    const recordFail = () => {
        const next = fails + 1;
        setFails(next);
        sessionStorage.setItem(FAIL_KEY, String(next));
        if (next >= MAX_FAILS) {
            const until = Date.now() + LOCK_MS;
            setLockUntil(until);
            setNow(Date.now());
            sessionStorage.setItem(LOCK_KEY, String(until));
        }
    };

    const clearLock = () => {
        setFails(0);
        setLockUntil(0);
        sessionStorage.removeItem(FAIL_KEY);
        sessionStorage.removeItem(LOCK_KEY);
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setNotice('');
        if (locked) {
            setError(`Demasiados intentos. Espera ${remaining}s.`);
            return;
        }
        const normalized = normalizeEmail(email);
        if (!isValidEmail(normalized)) {
            setError('Ingresa un correo válido.');
            return;
        }
        setLoading(true);
        try {
            await withBusy(async () => {
                await signInWithEmailAndPassword(auth, normalized, password);
            }, 'Iniciando sesión…');
            clearLock();
            sessionStorage.setItem('isNewLogin', 'true');
        } catch (err) {
            recordFail();
            setError(loginErrorMessage(err?.code));
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordReset = async () => {
        setError('');
        setNotice('');
        const normalized = normalizeEmail(email);
        if (!isValidEmail(normalized)) {
            setError('Ingresa tu correo en el campo de arriba para restablecer la contraseña.');
            return;
        }
        setLoading(true);
        try {
            await withBusy(async () => {
            const response = await fetch(`${AUTH_API_URL}/auth/request-password-reset`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: normalized }),
            });
            if (response.status === 429) {
                setError('Demasiados intentos. Espera unos minutos e inténtalo de nuevo.');
                return;
            }
            setNotice(GENERIC_RESET);
            }, 'Enviando solicitud…');
        } catch {
            setError('No se pudo enviar la solicitud. Revisa tu red e inténtalo de nuevo.');
        } finally {
            setLoading(false);
        }
    };

    const busy = loading || locked;

    return (
        <AuthShell
            kicker="Acceso"
            title="Iniciar sesión"
            subtitle="Usa la cuenta que te asignaron. No crees una nueva desde aquí."
        >
            <form onSubmit={handleLogin} autoComplete="on">
                <AuthField label="Correo" htmlFor="login-email">
                    <input
                        id="login-email"
                        name="email"
                        type="email"
                        inputMode="email"
                        autoComplete="username"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        maxLength={254}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="nina.v@example.com"
                        style={studio.control}
                        required
                    />
                </AuthField>
                <AuthField label="Contraseña" htmlFor="login-password">
                    <div style={{ position: 'relative' }}>
                        <input
                            id="login-password"
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            maxLength={128}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            style={{ ...studio.control, paddingRight: 48 }}
                            required
                        />
                        <PasswordToggle show={showPassword} onToggle={() => setShowPassword((v) => !v)} />
                    </div>
                </AuthField>
                <AuthNotice type="error">{error}</AuthNotice>
                <AuthNotice type="ok">{notice}</AuthNotice>
                <button
                    type="submit"
                    style={{
                        ...studio.primaryBtn,
                        width: '100%',
                        minHeight: 46,
                        fontSize: '1rem',
                        opacity: busy ? 0.7 : 1,
                        cursor: busy ? 'not-allowed' : 'pointer',
                    }}
                    disabled={busy}
                >
                    {locked ? `Bloqueado ${remaining}s` : loading ? 'Ingresando…' : 'Ingresar'}
                </button>
            </form>
            <button
                type="button"
                onClick={handlePasswordReset}
                style={{
                    ...studio.ghostBtn,
                    width: '100%',
                    marginTop: 12,
                    borderWidth: 0,
                    color: ink.accentText,
                    cursor: busy ? 'not-allowed' : 'pointer',
                }}
                disabled={busy}
            >
                ¿Olvidaste tu contraseña?
            </button>
        </AuthShell>
    );
};

export default Auth;
